/**
 * Standings: live camel rankings + per-player coin wallets. Used on the host
 * (TV) sidebar.
 */

import { CamelUp, PLAYER_COLOR_HEX, type RacingColor } from '@mdev/common'
import { CU, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'
import { Coins, Panel, PanelTitle } from './pieces'

type CamelUpState = CamelUp.CamelUpState

interface Props {
  state: CamelUpState
  highlightUid?: string
}

export function Standings(props: Props) {
  const { state, highlightUid } = props
  const rankings: RacingColor[] = CamelUp.rankRacingCamels(state)
  const activeUid = state.status === 'active' ? state.seatOrder[state.activeIndex] : undefined

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel>
        <PanelTitle>Camel rankings</PanelTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rankings.map((c, i) => (
            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  fontFamily: CU_HEADING,
                  fontWeight: 900,
                  fontSize: 16,
                  width: 22,
                  color: i === 0 ? CU.brass : CU.inkSoft,
                }}
              >
                {i + 1}
              </span>
              <CamelGlyph color={c} size={24} noShadow />
              <span style={{ fontFamily: CU_HEADING, textTransform: 'capitalize', color: CU.ink }}>
                {c}
              </span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel>
        <PanelTitle>Wallets</PanelTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {state.seatOrder.map((uid) => {
            const p = state.players[uid]
            const isActive = uid === activeUid
            const isHighlighted = uid === highlightUid
            return (
              <div
                key={uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 10,
                  background: isActive ? CU.surfaceSolid : 'transparent',
                  borderLeft: `4px solid ${PLAYER_COLOR_HEX[p.color]}`,
                  outline: isHighlighted ? `2px solid ${CU.accent}` : 'none',
                  boxShadow: isActive ? CU.shadowSoft : 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{p.avatar}</span>
                  <span
                    style={{
                      fontFamily: CU_HEADING,
                      fontWeight: isActive ? 600 : 500,
                      color: CU.ink,
                    }}
                  >
                    {p.displayName}
                  </span>
                  {isActive && (
                    <span
                      style={{
                        fontFamily: CU_HEADING,
                        fontSize: 10,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        background: CU.accent,
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: 6,
                      }}
                    >
                      Turn
                    </span>
                  )}
                </span>
                <Coins amount={p.coins} />
              </div>
            )
          })}
        </div>
      </Panel>
    </div>
  )
}
